export enum WindwardSlideType {
  general = 'general',
  perRecord = 'per-record',
}

export enum WindwardSlideSubType {
  relatedRecords = 'related-records',
}

/**
 * These are the options available in the map handler. You can use this interface
 * on a display function of a group with the data type configured as
 * {@link WindwardDataType.mapImage mapImage}. The value to be returned for this
 * type of display function must be an array of max three elements:
 *
 * [reportId, edpRecordId, mapHandlerOptions]
 *
 * @example
 *
 *  displayFn: (record: ReportRecord, options) => {
 *    const { reportId } = options;
 *    const edpRecordId = record.edpRecordId;
 *    return of([reportId, edpRecordId, {enable}]);
 *  }
 */
export interface MapHandlerOptions {
  /**
   * This attribute is used to let the server know that it should cache the map
   * image.
   * So far, there are two scenarios where we need a map image
   * 1. A single record (property, lease, sale, or availability)
   *    needs a map showing its location.
   * 2. We need a single map image showing all the records in a single slide
   *    (summary view)
   *
   * The latter scenario is the most common scenario where we would need this
   * configuration. The reason is that the framework works based on groups by
   * record. This means that the framework will run the map-image handler
   * unnecessarily per each record the report has.
   *
   * Default Value = false
   */
  enableCache?: boolean;
  /**
   * This is the max zoom (in) the map can go.
   * If the value is low, the map will be zoomed out.
   * if the value is high, the map will be zoomed in as much as the boundaries
   * allow it.
   *
   * Default Value = 16
   */
  maxZoom?: number;
  /**
   * This option will group any non-property-record by its associated property.
   * It will prevent showing multiple markers to be in the same spot for records
   * with shared property.
   *
   * It will not take any effect on the property reports
   *
   * Default Value = true
   */
  isGroupByProperty?: boolean;
  /**
   * This value will set the view port width in pixels of the map.
   *
   * Default Value = 500
   */
  width?: number;
  /**
   * This value will set the view port height in pixels of the map.
   *
   * Default Value = 500
   */
  height?: number;
}

/**
 * Values that must be provided in a blueprint groups (in the Windward Layout)
 * which the reporting server can use to identify how to transform the incoming
 * formatted data
 *
 * @see Layout.Windward
 * @see GroupBase
 */
export enum WindwardDataType {
  /**
   * will use the url provided in the "value" attribute from the incoming array
   * of {@link ResolvedTemplateField}
   */
  image = 'image',
  /**
   * String formed from the array of {@link ResolvedTemplateField}.
   * Specifically taken from the "value" attribute
   */
  header = 'header',
  /** a simple {@link ResolvedTemplateField} */
  resolvedField = 'resolved-field',
  /** list/array of {@link ResolvedTemplateField} */
  resolvedFieldList = 'resolved-field-list',
  /** there should be minimal to none changes to the received data structure */
  customData = 'custom-data',
  /**
   * this should be used to get the label string from the field.
   * Mainly used to get PPT section translations.
   * @example
   *
   * {
   *     "id": ###,
   *     "label": "Property Details", // this is the value to be returned
   *     "displayFnArguments": []
   * }
   */
  label = 'label',
  /**
   * this should be used to get a base 64 string with the image
   * of a map from all the records in a report or a single record.
   *
   * To be able to use this, the display function needs to return
   * an array of two elements (a tuple). the first element must be
   * the report id; and the second element (optional) must be the
   * edp record id.
   *
   * @example
   * [reportId, edpRecordId];
   */
  mapImage = 'map-image',
  /**
   * used to get headers for related records (nested records like availabilities in properties)
   */
  multipleHeader = 'multiple-header',
  /**
   * used to get resolved field array for related records (nested records like availabilities in properties)
   */
  multipleResolvedFieldList = 'multiple-resolved-field-list',
  /**
   * used to get resolved field objects for related records (nested records like availabilities in properties)
   */
  multipleResolvedField = 'multiple-resolved-field-list',
}

/**
 * @see GroupBase.resolveBy
 */
export enum WindwardResolveBy {
  push = 'push',
  replace = 'replace',
}

export enum PPTGroupsDataTypes {
  Image = 'image',
  Table = 'table',
  TableHorizontal = 'table-horizontal',
  TextBox = 'text-box',
  Shape = 'shape',
  CurrentDate = 'current-date',
}
